<template>
  <div>
    <!-- Widget Buttons -->
    <div class="widget-container">
      <div 
        class="tool" 
        @click="toggleSnippingTool" 
        :class="drawPolygon ? 'active' : ''"
        title="Draw Polygon"
        id="polygonWidget"
      >
        <fa :icon="['fas', 'crop']" class="icon" size="1x"/>
      </div>
      <div class="tool" :class="{ active: polygonActive }" @click="initModal" title="Upload Polygon" id="uploadWidget">
        <fa :icon="['fas', 'cloud-upload-alt']" class="icon" size="1x" />
      </div>
      <div class="tool" id="base-maps" :class="{ active: baseMapActive }" @click="selectNewBase"
        title="Toggle Base Map">
        <fa :icon="['fas', 'map']" class="icon" size="1x" />
      </div>
      <div class="tool" @click="toggleMeasure" :class="{ active: measuringToolOn }" title="Measuring Tool">
        <fa :icon="['fas', 'ruler']" class="icon" size="1x" />
      </div>
    </div>

    <!-- Modal -->
    <b-modal
      id="polygonInputModal"
      size="md"
      centered
      hide-header-close
      dialog-class="polygonInput-modal-dialog"
      content-class="polygonInput-modal-content"
      modal-header-class="polygonInput-modal-header"
      @show="reset()"
    >
      <!-- Modal Title -->
      <template #modal-header>
        <div class="polygonInput-header d-flex align-items-center w-100">
          <span class="polygonInput-header-icon">
            <fa :icon="['fas', 'draw-polygon']" />
          </span>
          <div class="polygonInput-header-text">
            <h5 class="polygonInput-title mb-0">Upload Geometry</h5>
            <small class="polygonInput-subtitle">Paste a WKT string or import a KML / KMZ file</small>
          </div>
        </div>
      </template>

      <!-- Overlay loader -->
      <div v-if="loadingText || loadingFile" class="modal-overlay">
        <b-spinner label="Loading..." variant="light" class="spinner-lg" />
      </div>

      <!-- Tabs -->
      <b-tabs
        card
        v-model="activeTab"
        nav-class="polygonInput-tabs-nav"
        active-nav-item-class="active-tab"
        class="polygonInput-tabs"
      >

        <!-- WKT Tab -->
        <b-tab title="WKT Upload">
          <div class="tab-pane-wrapper">
            <div class="tab-content-inner">
              <b-form-group class="form-block">
                <label class="field-label" for="wktTextarea">WKT geometry</label>
                <b-form-textarea
                  v-model="polygonInput"
                  placeholder="e.g. POLYGON ((-122.68 45.51, -122.68 45.52, ...))"
                  class="wkt-textarea"
                  ref="wktTextarea"
                  id="wktTextarea"
                  @input="resizeTextarea"
                />
                <small class="field-hint">Coordinates should be in longitude / latitude (EPSG:4326).</small>
              </b-form-group>
            </div>
          </div>
        </b-tab>

        <!-- File Upload Tab -->
        <b-tab title="KML / KMZ Upload">
          <div class="tab-pane-wrapper">
            <div class="tab-content-inner fileUploadControl">
              <b-form-group class="form-block" id="polygonShapeFile">
                <label class="field-label" for="polygonFileSelect">Geometry file</label>
                <b-form-file
                  ref="refPolygonShapeFile"
                  id="polygonFileSelect"
                  accept=".kml,.kmz"
                  placeholder="Select a file or drag it here..."
                  drop-placeholder="Drop file here..."
                  v-model="selectedFile"
                  @input="handleFileUpload"
                >
                  <!-- Custom Browse button content with spinner -->
                  <template #button-content>
                    <span class="cursor">
                      Browse
                      <b-spinner small v-if="loadingFile" class="mr-2" />
                    </span>
                  </template>
                </b-form-file>
                <small class="field-hint">Accepted formats: .kml, .kmz</small>
              </b-form-group>

              <!-- Optional display of selected file -->
              <div v-if="selectedFile" class="selected-file">
                <fa :icon="['fas', 'file-alt']" class="selected-file-icon" />
                <span class="selected-file-name">{{ selectedFile.name }}</span>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <!-- Footer with action buttons -->
      <template #modal-footer>
        <div class="polygonInput-footer">
          <b-button class="close-btn" @click="closePolygonInputModal()">
            Cancel
          </b-button>
          <b-button
            class="submit-btn"
            @click="renderPolygon"
            id="uploadSampleBtn"
            :disabled="!isValid || (loadingText && polygonInput == '') || (loadingFile && !selectedFile) || (!currentExtent)"
          >
            <fa :icon="['fas', 'cloud-upload-alt']" class="mr-2" />
            Upload
          </b-button>
        </div>
      </template>

    </b-modal>
  </div>
</template>

<style>
#uploadWidget.disabled {
  opacity: 0.5;
  pointer-events: none;
}

#polygonShapeFile .custom-file-label::after {
  cursor: pointer !important;
}
</style>

<style scoped>
/* =========================
   MODAL STYLING
   ========================= */
.polygonInput-modal-dialog {
  max-width: 35rem;
}

.polygonInput-modal-content {
  border: none;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 18px 45px rgba(6, 60, 109, 0.28);
}

::v-deep(.modal-header),
.polygonInput-modal-header {
  background: linear-gradient(120deg, #063C6D, #0A5490) !important;
  color: white !important;
  padding: 1.1rem 1.5rem;
  border-bottom: none;
}

/* =========================
   HEADER
   ========================= */
.polygonInput-header {
  gap: 0.85rem;
}

.polygonInput-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.625rem;
  height: 2.625rem;
  flex-shrink: 0;
  border-radius: 0.65rem;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 1.1rem;
}

.polygonInput-title {
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.polygonInput-subtitle {
  display: block;
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.75rem;
}

::v-deep(.polygonInput-tabs .nav-link) {
  color: #6c757d !important;
  font-weight: 500;
  border: none !important;
  border-radius: 0.5rem 0.5rem 0 0 !important;
  transition: background 0.35s ease, color 0.25s ease;
  background: linear-gradient(90deg, rgba(6,60,109,0.15), rgba(6,60,109,0.05)) !important;
}

/* On hover — slightly brighter dull gradient */
::v-deep(.polygonInput-tabs .nav-link:not(.active):hover) {
  color: #063C6D !important;
  background: linear-gradient(90deg, rgba(6,60,109,0.3), rgba(6,60,109,0.15)) !important;
}

/* Active tab — strong gradient highlight */
::v-deep(.polygonInput-tabs .nav-link.active),
::v-deep(.polygonInput-tabs .active-tab) {
  color: white !important;
  background: linear-gradient(90deg, #063C6D, #063C6D, #063C6D) !important;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

/* Tabs */
.polygonInput-tabs-nav {
  background: #6A8AA7;
  border-bottom: 2px solid #6A8AA7;
}

/* Tab content */
.tab-pane-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 1rem 1rem;
  min-height: 0.625rem;
  transition: all 0.3s ease;
  /* set this to fit the tallest tab comfortably */
}

.tab-content-inner {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 37.5rem;
}

/* Field labels & hints */
.field-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #063C6D;
}

.field-hint {
  display: block;
  margin-top: 0.4rem;
  font-size: 0.72rem;
  color: #8a97a3;
}

/* Selected file chip */
.selected-file {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: center;
  margin-top: 0.85rem;
  padding: 0.4rem 0.85rem;
  max-width: 100%;
  border-radius: 999px;
  background: rgba(6, 60, 109, 0.08);
  color: #063C6D;
  font-size: 0.8rem;
}

.selected-file-icon {
  flex-shrink: 0;
}

.selected-file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

/* =========================
   FOOTER / BUTTONS
   ========================= */
.polygonInput-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
}

.submit-btn {
  min-height: 2.5rem;
  min-width: 8.125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none !important;
  border-radius: 999px !important;
  font-size: 0.85rem;
  font-weight: 600;
  background-color: #063C6D !important;
  box-shadow: 0 4px 12px rgba(6, 60, 109, 0.25);
  transition: background-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  background-color: #0A5490 !important;
  box-shadow: 0 6px 16px rgba(6, 60, 109, 0.35);
}

.submit-btn:disabled {
  opacity: 0.5;
  box-shadow: none;
}

/* =========================
   OVERLAY LOADER
   ========================= */
.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

/* =========================
   ORIGINAL WIDGET / FILE CSS
   ========================= */
.widget-container {
  background-color: #063c6d;
  height: 2.5rem;
  width: 14.375rem;
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(6, 60, 109, 0.25);
  display: flex;
  align-items: center;
  overflow: hidden;
}

.widget-container .tool {
  height: 100%;
  flex: 1 1 0;
  padding: 0;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}

.widget-container .tool:last-child {
  border-right: none;
}

.widget-container .tool:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.widget-container .tool .icon {
  height: 0.875rem;
  width: 0.875rem;
}

.widget-container .tool.active {
  background: rgba(255, 255, 255, 0.22) !important;
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

.uploadWktContainer {
  margin: 0.625rem;
}

.fileUploadControl {
  margin: 0.9375rem 0.625rem 0.9375rem 0.625rem;
}

.file_upload_Loader {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  gap: 2%;
  z-index: 10;
  position: absolute;
  background: radial-gradient(rgba(3, 98, 151, 0.8), rgba(0, 0, 0, 0.5));
  width: 90%;
  height: 3rem;
  top: 30%;
}

.wkt-textarea {
  min-height: 6.25rem;
  max-height: 18.75rem;
  overflow-y: hidden;
  resize: none;
  border-radius: 0.6rem;
  border: 1px solid #d4dbe2;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.82rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.wkt-textarea:focus {
  border-color: #0A5490;
  box-shadow: 0 0 0 0.15rem rgba(10, 84, 144, 0.15);
}

.close-btn {
  font-size: 0.85rem !important;
  font-weight: 600;
  color: #063C6D !important;
  background-color: transparent !important;
  border: 1px solid #cdd6df !important;
  border-radius: 999px !important;
  min-height: 2.5rem;
  min-width: 6.25rem;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.close-btn:hover {
  background-color: rgba(6, 60, 109, 0.06) !important;
  border-color: #063C6D !important;
  color: #063C6D !important;
}

</style>

<script>
import WKT from 'ol/format/WKT';
import CircleStyle from 'ol/style/Circle'
import { Draw, Snap } from 'ol/interaction'
import Fill from 'ol/style/Fill'
import LineString from 'ol/geom/LineString'
import Polygon from 'ol/geom/Polygon'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { ref } from 'vue'
import {getLength, getArea, getDistance} from 'ol/sphere'
import axios from 'axios'
import JSZip from 'jszip'
import KML from 'ol/format/KML'
import GeometryCollection from 'ol/geom/GeometryCollection'
import MultiPolygon from 'ol/geom/MultiPolygon'
import {
  get as projGet,
  fromLonLat,
  toLonLat,
  transform,
  transformExtent
} from 'ol/proj'
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
export default {
  name: 'DT_Widgets',
  props: [
    'map',
    'currentBaseMap',
    'selectedExtent',
    'user',
    'polygonsSelected',
    'tutorialTrigger',
    'ptvMapFlag',
    'lidarFlag'
  ],
  data() {
    return {
      selectedFile: null,
      polygonInput: null,
      polygonFeature: null,
      isValid: false,
      wktObject: null,
      polygonActive: false,
      baseMapActive: false,
      baseMaps: [
        {
          name: 'Azure',
          src: require('./../../../assets/base_maps/azureSatellite.png')
        },
        {
          name: 'OSM',
          src: require('./../../../assets/base_maps/OpenStreetMapOriginal.png')
        }
      ],
      measuringToolOn: false,
      draw: null,
      measurement: ['0', 'm'],
      drawPolygon: false,
      polygonMeasurement: 0.0,
      polygonWkt: null,
      currentExtent: undefined,
      allowedFileExtensions: ['kml', 'kmz'],
      allowedKmlGeometries: ['Polygon', 'MultiPolygon', 'LineString', 'MultiLineString'],
      isValidKmlGeometry: false,
      activeTab: 0,        // 0 = WKT, 1 = File Upload
      loadingText: false,
      loadingFile: false,
      selected: null,
      isFileUpload: false,
      extractedPcIds: []
    }
  },
  methods: {
    resizeTextarea() {
      const el = this.$refs.wktTextarea.$el || this.$refs.wktTextarea;
      if (!el) return;
      el.style.height = 'auto'; // Reset height so it can shrink if needed
      el.style.height = Math.min(el.scrollHeight, 300) + 'px'; // Expand up to max-height (300px)
    },
    selectNewBase(base) {
      this.baseMapActive = !this.baseMapActive;
      this.$emit('toggleBaseLayer', this.baseMapActive ? 'Azure' : 'OpenStreetMap')
    },
    reset() {
      this.isValid = false;
      this.polygonInput = ''
      this.selectedFile = null
      this.polygonFeature = null
      this.loadingFile = false
      this.loadingText = false
      this.extractedPcIds = []
      if (this.$refs.refPolygonShapeFile) {
        this.$refs.refPolygonShapeFile.reset()
      }
    },
    closePolygonInputModal() {
      this.reset()
      this.$bvModal.hide('polygonInputModal')
    },
    initModal() {
      this.$bvModal.show('polygonInputModal')
    },
    async renderPolygon() {
      if (!this.polygonFeature || !this.currentExtent) return

      const format = new WKT()

      // normalize geometry → WKT (for backend / lidar)
      const geom =
        Array.isArray(this.polygonFeature)
          ? this.polygonFeature[0].getGeometry()
          : this.polygonFeature.getGeometry()

      const wkt = format.writeGeometry(geom, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857'
      })

      // always emit polygon first
      this.$emit('addPolygon', {
        featureInput: this.polygonFeature,
        isFileUpload: this.isFileUpload
      })

      // LIDAR logic centralized here
      if (this.lidarFlag) {
        if (this.extractedPcIds.length) {
          this.$emit('lidarSelectionByPcIds', this.extractedPcIds)
        } else {
          // This code just checks for 10km^2 WKT size. Commented out for now. 
          // const isValid = await this.checkSize(geom)
          // if (!isValid) return
          this.$emit('lidarSelection', { wkt, geom: this.polygonFeature })
        }
      }

      this.$bvModal.hide('polygonInputModal')
    },
    checkPolygon(text) {
      try {
        if (text) {
          if (Array.isArray(text)) {
            // WEBGIS-3793 10/07/25 - Handle multiple polygon upload from a single file
            this.polygonFeature = text.map(wktText =>
              this.wktObject.readFeature(wktText, {
                dataProjection: 'EPSG:4326',
                featureProjection: 'EPSG:3857'
              })
            )
            this.polygonWkt = text[0]
          } else {
            this.loadingText = true
            // WEBGIS-3793 10/07/25 - Handle Single polygon upload
            this.polygonFeature = this.wktObject.readFeature(text, {
              dataProjection: 'EPSG:4326',     // Your coords are in lon/lat
              featureProjection: 'EPSG:3857'   // Web Mercator for display
            })
            this.polygonWkt = text
          }

          this.isValid = true
          this.getExtentRelease(this.polygonWkt)
          this.loadingText = false
        }
      } catch (err) {
        console.log("err => ", err)
        this.loadingText = false
        this.loadingFile = false
        this.polygonWkt = null

        if (!this.selectedFile) {
          this.$bvToast.toast('Please Upload a Valid WKT.', {
            autoHideDelay: 7000,
            variant: 'danger',
            solid: true,
            title: 'Invalid WKT',
            toaster: 'b-toaster-bottom-right'
          })
        } else{
          this.$bvToast.toast('Please Upload a KML/KMZ with Valid WKT.', {
            autoHideDelay: 7000,
            variant: 'danger',
            solid: true,
            title: 'Invalid KML/KMZ',
            toaster: 'b-toaster-bottom-right'
          })

        }
      }
    },
    formatPolygonArea(polygon) {
      const area = getArea(polygon, {
        projection: 'EPSG:3857'
      }) // m²

      const km2 = (area / 1_000_000).toFixed(2) + ' km²'

      return [km2]
    },
    async toggleMeasure() {
      this.measuringToolOn = !this.measuringToolOn
      this.$emit('toggleMeasuringTool')
      if (this.measuringToolOn) {
        if (this.drawPolygon) {
          this.toggleSnippingTool()
        }
        this.addInteraction()
        this.$emit('measurement', this.measurement)
      } else {
        this.$emit('addPolygonSelect')
        this.map.removeInteraction(this.draw)
        this.measurement = ['0', 'm']
      }
    },
    addPolygonInteraction() {
      this.$emit('removePolygonSelect')
      this.polygonMeasurement = ['0.00 km²']
      this.$emit('measurement', this.polygonMeasurement)
      let snap
      const source = new VectorSource()
      var vectorLayer = new VectorLayer({
        source: source,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.6)'
          }),
          stroke: new Stroke({
            color: '#ffcc33',
            width: 4
          }),
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({
              color: '#ffcc33'
            })
          })
        })
      })
      this.draw = new Draw({
        source: source,
        type: 'Polygon'
      })
      this.map.addInteraction(this.draw)
      snap = new Snap({source: source})
      this.map.addInteraction(snap)
      vectorLayer.setZIndex(4)
      this.$emit('getVectorlayer', vectorLayer)
      let self = this
      let listener
      this.draw.on('drawstart', function (evt) {
        // Clear cart and polygons
        if (self.lidarFlag) {
          self.$emit('clearCart');
          self.$emit('clearPolygon');
        }
        
        listener = evt.feature.getGeometry().on('change', function (evt) {
          var geom = evt.target
          // self.polygonMeasurement = self.formatPolygonArea(geom).toFixed(2)
          self.polygonMeasurement = self.formatPolygonArea(geom)
          self.$emit('measurement', self.polygonMeasurement)
        })
      })
      this.draw.on('drawend', async function (evt) {
        const format = new WKT()
        self.polygonWkt = null
        self.polygonWkt = format.writeGeometry(
          evt.feature.getGeometry(),
          {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857'
          }
        )

        self.polygonFeature = self.wktObject.readFeature(self.polygonWkt, {
          dataProjection: 'EPSG:4326',     // Your coords are in lon/lat
          featureProjection: 'EPSG:3857'   // Web Mercator for display
        })

        if (self.lidarFlag) {
          const isValid = await self.checkSize(evt.feature.getGeometry())
          if (!isValid) {
            return;
          }
          self.$emit('lidarSelection', {wkt: self.polygonWkt, geom: self.polygonFeature})
        }

        self.$emit('addPolygon',  {featureInput: self.polygonFeature, isFileUpload: self.isFileUpload})
        
        self.toggleSnippingTool()
      })
    },
    checkSize(geometry) {
      // Calculate geodesic area (more accurate for lat/lon)
      const area = getArea(geometry, {
        projection: 'EPSG:3857'
      })

      let maxSize = 10; // 10 km²

      const maxArea = maxSize * 1000 * 1000

      if (area > maxArea) {
        // Example handling:
        this.$bvToast.toast('Polygon must be 10 km² or smaller.', {
          title: 'Invalid Area',
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          autoHideDelay: 10000,
          solid: true
        })

        return false
      }

      return true
    },
    addInteraction() {
      this.$emit('removePolygonSelect')
      var sketch
      var source = new VectorSource()
      var vector = new VectorLayer({
        source: source,
        zIndex: 200,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)'
          }),
          stroke: new Stroke({
            color: '#ffcc33',
            width: 2
          }),
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({
              color: '#ffcc33'
            })
          })
        })
      })
      var type = 'LineString'
      this.draw = new Draw({
        source: source,
        type: type,
        style: new Style({
          fill: new Fill({
            color: 'rgba(255, 2, 2, 0.2)'
          }),
          stroke: new Stroke({
            color: 'rgba(255, 2, 2, 0.5)',
            lineDash: [10, 10],
            width: 2
          }),
          image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(255, 2, 2, 0.7)'
            }),
            fill: new Fill({
              color: 'rgba(255, 2, 2, 0.2)'
            })
          })
        })
      })
      this.map.addInteraction(this.draw)
      const self = this
      this.draw.on('drawstart', function (evt) {
        // Set sketch
        sketch = evt.feature
        source.clear()
        let listener = sketch.getGeometry().on('change', function (evt) {
          var geom = evt.target
          var output
          if (geom instanceof Polygon) {
            output = self.formatArea(geom) // REFACTOR SUGGESTION -- formatArea is not a fn...?
          } else if (geom instanceof LineString) {
            output = self.formatLength(geom)
          }

          // output[0] is m/km, output[1] is ft/mi
          self.measurement = output[0].split(' ')
          self.$emit('measurement', self.measurement)
        })
      })

      this.draw.on('drawend', function () {
        sketch = null
      })
    },
    formatLength(line) {
      const coordinates = line.getCoordinates();
      let totalLength = 0;

      for (let i = 0; i < coordinates.length - 1; i++) {
        const c1 = transform(coordinates[i], 'EPSG:3857', 'EPSG:4326');
        const c2 = transform(coordinates[i + 1], 'EPSG:3857', 'EPSG:4326');
        totalLength += getDistance(c1, c2);
      }

      var length = getLength(line)
      var output
      var meters
      var feet
      if (length > 100) {
        meters = `${Math.round((length / 1000) * 100) / 100} km`
        feet = `${Math.round((length / 1609.344) * 100) / 100} miles`
      } else {
        meters = `${Math.round(length * 100) / 100} m`
        feet = `${Math.round(length * 3.281 * 100) / 100} feet`
      }
      output = [meters, feet]
      return output
    },
    formatArea(polygon) {
      const area = getArea(polygon) //distance between the points (in meters).
      let output
      if (area > 10000) {
        output = Math.round((area / 1000) * 100) / 100
      } else {
        output = Math.round((area / 1000) * 100) / 100
      }
      return output * 0.621371 // convert to miles
    },
    async toggleSnippingTool() {
      this.drawPolygon = !this.drawPolygon
      this.$emit('toggleSnippingTool')
      this.$emit('toggleMeasuringTool')
      if (this.drawPolygon) {
        if (this.measuringToolOn) {
          this.toggleMeasure()
        }
        this.addPolygonInteraction()
        this.$emit('measurement', this.polygonMeasurement)
      } else {
        this.map.removeInteraction(this.draw)
        this.drawPolygon = false
        this.polygonMeasurement = 0
        this.$emit('addPolygonSelect')
      }
    },
    getExtentRelease(poly) {
      let body = {
        polygon: poly
      }

      let apiEndpoint = this.lidarFlag ? 'trailReviewTool/get_extent' : 'dataDeliveryTool/get_extent'

      if (sessionStorage.token) {
        axios
          .post(process.env.API_LOCATION + `/api/${apiEndpoint}`, body, {
            headers: {
              'x-auth-token': sessionStorage.token
            }
          })
          .then(response => {
            if (response.data) {
              this.currentExtent = response.data   
            } else {
              this.currentExtent = null
              this.currentExtent = ''
            }       
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    generateTutorial() {
      this.driverObj = driver({
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        steps: [
          { 
            element: '#polygonWidget', 
            popover: { title: 'Sample Generation Tutorial', description: 'Click on the draw polygon widget to toggle it on.', side: "left", align: 'start' }
          },
          { 
            element: '#ptv-map', 
            popover: { title: 'Draw Sample', description: 'Click and drag to add points to the map to create a polygon.', side: "bottom", align: 'start' }
          },
          { 
            element: '#polygonWidget', 
            popover: { 
              title: 'Draw Multiple Samples', 
              description: 'Feel free to toggle the tool back on and create more than one polygon on the map!', 
              side: "bottom", 
              align: 'start' 
            }
          },
          {
            element: '#uploadWidget',
            popover: {
              title: 'Upload Sample Extent', 
              description: 'As an alternative to drawing a sample, you can click on this widget to upload a WKT or a KML/KMZ file.', 
              side: "bottom", 
              align: 'start',
              onNextClick: async () => {
                await this.$bvModal.show('polygonInputModal')
                this.activeTab = 0
                this.$nextTick(() => {
                  this.driverObj.moveNext()
                })
              }
            }
          },
          {
            element: '#wktTextarea',
            popover: {
              title: 'Upload WKT', 
              description: 'Paste a WKT geometry here.', 
              side: "bottom", 
              align: 'start',
              onNextClick: () => {
                this.activeTab = 1
                this.$nextTick(() => {
                  this.driverObj.moveNext()
                })
              },
              onPrevClick: () => {
                this.$bvModal.hide('polygonInputModal')
                this.$nextTick(() => {
                  this.driverObj.movePrevious()
                })
              }
            }
          },
          {
            element: '#polygonFileSelect',
            popover: {
              title: 'Upload KML/KMZ File', 
              description: 'Select or drag a KML/KMZ file from your device here.', 
              side: "bottom", 
              align: 'start',
              onPrevClick: () => {
                this.activeTab = 0
                this.$nextTick(() => {
                  this.driverObj.movePrevious()
                })
              }
            }
          },
          {
            element: '#uploadSampleBtn',
            popover: {
              title: 'Upload Sample', 
              description: 'After uploading a file or WKT, click the Upload button to view the extent on the map.', 
              side: "bottom", 
              align: 'start',
              onNextClick: () => {
                this.$bvModal.hide('polygonInputModal')
                this.$nextTick(() => {
                  this.driverObj.moveNext()
                })
              }
            }
          },
          { 
            element: '#ptv-map', 
            popover: { 
              title: 'Select Sample(s)', 
              description: 'After creating your sample polygons on the map, use your mouse to select which samples you want to generate statistics for.', 
              side: "left", 
              align: 'start',
              onPrevClick: () => {
                this.$bvModal.show('polygonInputModal')
                this.$nextTick(() => {
                  this.driverObj.movePrevious()
                })
              } 
            }
          },
          { 
            element: '#statsBtn', 
            popover: { 
              title: 'Generate Statistics', 
              description: 'When one or more sample polygons have been selected, click the statistics button to view the sample statistics', 
              side: "top", 
              align: 'start' 
            }
          }
        ],
        onDestroyStarted: () => {
          this.$emit('acknowledgeTutorial', 'PTV')
          this.driverObj.destroy();
        }
      });
    },
    startTutorial() {
      this.driverObj.drive();
    },
    async handleFileUpload(file) {
      if (!file) {
        return;
      }

      this.selectedFile = file

      if (this.selectedFile) {
        this.isValidKmlGeometry = false
        this.loadingFile = true
        const ext = this.selectedFile.name.split('.').pop().toLowerCase()
        let kmlText = ''

        if (this.allowedFileExtensions.includes(ext.toLowerCase())) {
          try {
            if (ext.toLowerCase() === 'kmz') {
              //KMZ → unzip → extract first .kml
              const zip = await JSZip.loadAsync(this.selectedFile)
              const kmlFile = Object.keys(zip.files).find(f => f.endsWith('.kml'))
              if (!kmlFile) {
                console.log('No KML found inside KMZ')
                throw new Error("No KML found inside KMZ.")
              }
              kmlText = await zip.files[kmlFile].async('text')
            } else {
              // Directly read KML
              kmlText = await this.selectedFile.text()
            }
            const { wkts: kmlToWKTText, pcIds } = await this.kmlToWKT(kmlText)
            this.extractedPcIds = pcIds
            if (kmlToWKTText) {
              const containsSubstring = kmlToWKTText.some(str => 
                this.allowedKmlGeometries.some(sub => 
                str.toLowerCase().includes(sub.toLocaleLowerCase())))         

              if (containsSubstring) {
                this.checkPolygon(kmlToWKTText)
                this.loadingFile = false
                this.isFileUpload = true
              } else {
                this.isValidKmlGeometry = true
                this.loadingFile = false
                this.isValid = false
                this.$nextTick(() => {
                  this.selectedFile = null
                })

                this.$bvToast.toast('The KML/KMZ file does not have Valid Geometries to display. ', {
                  autoHideDelay: 7000,
                  variant: 'danger',
                  solid: true,
                  title: 'Invalid KML/KMZ ',
                  toaster: 'b-toaster-bottom-right'
                })
              }
            }
            else {
              console.log("Invalid KML/KMZ file")
              throw new Error("No KML found inside KMZ.")
            }

          } catch (err) {
            this.isValid = false
            this.loadingFile = false
            this.isValid = false
            this.$nextTick(() => {
              this.selectedFile = null
            })
                
            this.$bvToast.toast('Please Upload a KML/KMZ with Valid WKT. ' + err, {
              autoHideDelay: 7000,
              variant: 'danger',
              solid: true,
              title: 'Invalid KML/KMZ',
              toaster: 'b-toaster-bottom-right'
            })
            
          }
        } else {
          this.isValid = false
          this.loadingFile = false
          this.isValid = false
          this.$nextTick(() => {
            this.selectedFile = null
          })

          this.$bvToast.toast('Only valid KML/KMZ files are allowed. ', {
            autoHideDelay: 7000,
            variant: 'danger',
            solid: true,
            title: 'Invalid KML/KMZ',
            toaster: 'b-toaster-bottom-right'
          })
        }
      } else {
        this.isValid = false
        this.loadingFile = false
        this.isValid = false        
        this.$nextTick(() => {
          this.selectedFile = null
        })

        this.$bvToast.toast('Please Upload valid KML/KMZ file. ', {
          autoHideDelay: 7000,
          variant: 'danger',
          solid: true,
          title: 'Invalid KML/KMZ',
          toaster: 'b-toaster-bottom-right'
        })
      }
    },

    // Convert KML to Geometry text
    async kmlToWKT(kmlText) {
      const kmlFormat = new KML()
      const wktFormat = new WKT()
      const wkts = []
      const pcIds = []

      // Read KML into features Geometry
      const features = kmlFormat.readFeatures(kmlText, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:4326'
      })

      features.forEach(f => {
        // Collect pc_id from ExtendedData if present (written by TRT KMZ export)
        const pcId = f.get('pc_id')
        if (pcId) pcIds.push(pcId)

        let geom = f.getGeometry()

        // Handle GeometryCollection / MultiGeometry
        if (geom instanceof GeometryCollection) {
          const polygons = []

          geom.getGeometries().forEach(g => {
            if (g instanceof Polygon) {
              // Force 2D coordinates
              const coords = g.getCoordinates().map(ring =>
                ring.map(c => [c[0], c[1]])
              )
              polygons.push(coords)
            }
          })

          if (polygons.length === 1) {
            geom = new Polygon(polygons[0])
          } else if (polygons.length > 1) {
            geom = new MultiPolygon(polygons)
          }
        } else if (geom instanceof Polygon) {
          // Force 2D coordinates
          const coords = geom.getCoordinates().map(ring =>
            ring.map(c => [c[0], c[1]])
          )
          geom = new Polygon(coords)
        }

        // Write clean WKT
        wkts.push(wktFormat.writeGeometry(geom, { decimals: 6 }))
      })

      // Only trust pcIds if every feature carried one — guarantees a TRT-exported KMZ
      return { wkts, pcIds: pcIds.length === features.length && features.length > 0 ? pcIds : [] }
    }
  },
  watch: {
    polygonInput: function (newValue) {
      if (newValue) {
        this.checkPolygon(newValue)
      } else {
        this.isValid = false
      }
    },
    currentExtent: function (newValue) {
      // Clear polygon if 
      if (newValue) {
        if (newValue.extent_description != this.selectedExtent) {
          this.$emit('clearPolygon')
        }

        this.$emit('datasetSelected', this.currentExtent)
      } else {
        this.$bvToast.toast('The provided KMZ file exists in a region unavailable for this tool.', {
          autoHideDelay: 10000,
          variant: 'danger',
          solid: true,
          title: 'Region Not Available',
          toaster: 'b-toaster-top-right'
        })
      }
    },
    activeTab(newVal, oldVal) {
      this.activeTab = newVal;
      if (oldVal != newVal) {
        this.reset()
      }
      this.$nextTick(() => {
        this.resizeTextarea();
      })
    },
    tutorialTrigger: function (newValue) {
      if (newValue && !this.user.tutorial_acknw["PTV"] && !this.lidarFlag) {
        this.startTutorial()
      }
    },
    driverObj(newVal) {
      this.generateTutorial();
    }
  },
  mounted() {
    this.wktObject = new WKT()
    this.generateTutorial()
  },
  created() {
    this.reset()
    this.selected = 'uploadWkt'
  },
  beforeDestroy() {
    this.driverObj.destroy();
  }
}
</script>